import React, { useCallback, useMemo, useState } from 'react';
import cn from 'classnames';
import Container from 'components/UI/Container';
import Loader from 'components/UI/Loader';
import Title from 'components/UI/Title';
import Subtitle from 'components/UI/Subtitle';
import Slider from 'components/UI/Slider';
import { CustomDot, CustomPagination } from 'components/UI/Slider/utils';
import CustomProjectGalleryCard from 'components/cards/CustomProjectGalleryCard';
import { useProjectsCards } from 'components/Cases';
import { useLang } from 'utils/localisation';
import { withErrorBoundary } from 'components/ErrorBoundary';

import * as styles from './ProjectsSlider.module.scss';

function ProjectsSlider({ title, description, projectsKey, customTitles }) {
  const lang = useLang();
  const preparedLang = lang === 'uk' || lang === 'kz' ? 'en' : lang;
  const { inViewRef, projects, loading } = useProjectsCards(projectsKey, preparedLang);
  const [current, setCurrent] = useState(0);

  const setCurrentKey = useCallback((prev, next) => {
    setCurrent(next);
  }, []);

  const settings = useMemo(
    () => ({
      initialSlide: 0,
      customPaging: CustomDot.bind(null, projects.length, current),
      appendDots: CustomPagination,
      dotsClass: 'slick-pagination',
      dots: true,
      infinite: projects?.length > 1,
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: true,
      responsive: [
        {
          breakpoint: 959,
          settings: {
            arrows: false,
          },
        },
      ],
    }),
    [projects.length, current],
  );

  return (
    <Container id="concepts-list">
      <div className={styles.layout} ref={inViewRef}>
        <Title tag="h2">{title}</Title>
        {description && <Subtitle size="lg">{description}</Subtitle>}
        <div className={cn('defaultSlider', styles.slider)}>
          {loading ? (
            <Loader theme="fullHeight" />
          ) : (
            <Slider {...settings} beforeChange={setCurrentKey}>
              {projects.map(project => (
                <CustomProjectGalleryCard
                  key={project.id}
                  theme="lgWidth"
                  {...project}
                  title={customTitles?.[project.id] ?? project.title}
                />
              ))}
            </Slider>
          )}
        </div>
      </div>
    </Container>
  );
}

ProjectsSlider.displayName = 'ProjectsSlider';

export default withErrorBoundary(ProjectsSlider);
